import { fetchGlobalRoles } from "features/Accounts/CreateAccount/state/thunks/createAccountThunk";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { type GlobalRole } from "interfaces/role";
import { forEach } from "lodash";
import { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Label } from "semantic-ui-react";
import FormSectionHeader from "components/forms/FormSectionHeader";
import type ValidatedFormProps from "../../../../components/forms/validatedFormProps";
import type NewContact from "../../../../interfaces/NewContact";
import AddContact from "./AddContact/AddContact";
import ContactList from "./ContactList/ContactList";
import "./Contacts.scss";

export interface ContactsProps extends ValidatedFormProps {
  contacts: NewContact[];
  onContactsChanged: (contacts: Array<NewContact>) => void;
}

const getMainContactRoles = (contact: NewContact, roles: GlobalRole[]) =>
  roles.filter((role) => role.isMainContactRole && !contact.roles.some((assignedRole) => assignedRole.id === role.id));

const Contacts = ({
  contacts,
  isLoadingGlobalRoles = false,
  accountTypeId,
  onFetchGlobalRoles,
  globalRoles,
  onContactsChanged,
}: ContactsProps & PropsFromRedux) => {
  useEffect(() => {
    onFetchGlobalRoles(accountTypeId);
  }, [accountTypeId, onFetchGlobalRoles]);

  const addContact = (contact: NewContact) => {
    const newContact = { ...contact, id: contacts.length, isMain: false };

    if (contacts.length === 0) {
      const mainRoles = getMainContactRoles(newContact, globalRoles);

      newContact.roles = [...newContact.roles, ...mainRoles];
      newContact.autoAssignedRoleIds = mainRoles.map((role) => role.id);
      newContact.isMain = true;
    }

    const newContacts = [...contacts, newContact];

    onContactsChanged(newContacts);
  };

  const removeContact = (contactId: number) => {
    const newContacts = contacts.filter((c) => c.id !== contactId);
    forEach(newContacts, (contact, index) => (contact.id = index));

    onContactsChanged(newContacts);
  };

  const setMainContact = (contactId: number) => {
    const newContacts = contacts.map((contact) => {
      if (contact.id !== contactId && contact.isMain) {
        return {
          ...contact,
          isMain: false,
          autoAssignedRoleIds: [],
          roles: contact.roles.filter((role) => !contact.autoAssignedRoleIds.some((id) => id === role.id)),
        };
      }

      if (contact.id === contactId) {
        const mainRoles = getMainContactRoles(contact, globalRoles);
        return {
          ...contact,
          isMain: true,
          roles: [...contact.roles, ...mainRoles],
          autoAssignedRoleIds: mainRoles.map((role) => role.id),
        };
      }

      return contact;
    });

    onContactsChanged(newContacts);
  };

  const renderBadge = () => {
    if (contacts.length > 0) {
      return (
        <Label circular color="blue" className="badge">
          {contacts.length}
        </Label>
      );
    }

    return null;
  };

  return (
    <section className="contact-form">
      <FormSectionHeader title="Add contacts" />
      <AddContact
        contacts={contacts}
        onContactAdded={addContact}
        globalRoles={globalRoles}
        isLoadingGlobalRoles={isLoadingGlobalRoles}
        isReadOnly={false}
      />
      <div className="contacts-overview">
        <div className="title-container">
          <FormSectionHeader title="Contacts" />
          {renderBadge()}
        </div>
        <ContactList contacts={contacts} onContactRemoved={removeContact} onMainContactSet={setMainContact} />
      </div>
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { creation } = state.accounts;
  const { userProfile } = state;

  return {
    globalRoles: creation.globalRoles.items,
    isLoadingGlobalRoles: creation.globalRoles.isLoading,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    accountTypeId: creation.configuration.accountTypeId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    onFetchGlobalRoles: bindAction(fetchGlobalRoles, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Contacts);

export default ConnectedComponent;
